import React, { useState, useEffect, useCallback } from 'react';
import { settingsApi, stopsApi, challengesApi, teamsApi, manualPointsApi, adminApi } from '../services/api';

const SESSION_KEY = 'rebus_admin_auth';
const ADMIN_PASSWORD = 'karlerik';

// ── Password gate ────────────────────────────────────────────────────────────

function LoginCard({ onLogin }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === ADMIN_PASSWORD) {
      onLogin();
    } else {
      setError('Feil passord. Prøv igjen.');
    }
  };

  return (
    <div className="page admin-page">
      <div className="admin-login-card">
        <h1>Admin</h1>
        <p className="admin-subtitle">Skriv inn passordet for å fortsette.</p>
        <form onSubmit={handleSubmit} className="admin-form">
          <input
            type="password"
            className="admin-input"
            placeholder="Passord"
            value={input}
            onChange={e => setInput(e.target.value)}
            autoFocus
          />
          {error && <p className="admin-error">{error}</p>}
          <button type="submit" className="admin-btn">Logg inn</button>
        </form>
      </div>
    </div>
  );
}

// ── Single stop row ──────────────────────────────────────────────────────────

const MEDIA_BASE = process.env.REACT_APP_MEDIA_URL || '';

function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('/media/')) return `${MEDIA_BASE}${url}`;
  return url;
}

function StopRow({ stop, answers, onDeleted, isGoal, onSetGoal, goalSaving }) {
  const [points, setPoints] = useState(String(stop.points));
  const [challenge, setChallenge] = useState(stop.challenge_text || '');
  const [question, setQuestion] = useState(stop.question || '');
  const [lat, setLat] = useState(String(stop.latitude));
  const [lng, setLng] = useState(String(stop.longitude));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [expanded, setExpanded] = useState(false);

  const [currentImageUrl, setCurrentImageUrl] = useState(stop.image_url || '');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isDirty =
    points !== String(stop.points) ||
    challenge !== (stop.challenge_text || '') ||
    question !== (stop.question || '') ||
    lat !== String(stop.latitude) ||
    lng !== String(stop.longitude);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await stopsApi.adminUpdate(
        stop.id,
        { points: parseInt(points, 10), challenge_text: challenge, question, latitude: parseFloat(lat), longitude: parseFloat(lng) },
        ADMIN_PASSWORD,
      );
      setMsg('Lagret!');
    } catch (err) {
      setMsg(err.response?.data?.error || 'Noe gikk galt.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setUploadMsg('');
  };

  const handleUpload = async () => {
    if (!imageFile) return;
    setUploading(true);
    setUploadMsg('');
    try {
      const res = await stopsApi.uploadImage(stop.id, imageFile, ADMIN_PASSWORD);
      setCurrentImageUrl(res.data.image_url || '');
      setImageFile(null);
      setImagePreview(null);
      setUploadMsg('Bilde lastet opp!');
    } catch (err) {
      setUploadMsg(err.response?.data?.error || 'Opplasting feilet.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Slett "${stop.title}"? Dette kan ikke angres.`)) return;
    setDeleting(true);
    try {
      await stopsApi.delete(stop.id, ADMIN_PASSWORD);
      onDeleted(stop.id);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Sletting feilet.');
      setDeleting(false);
    }
  };

  const stopAnswerEntry = answers.find(a => a.stop_id === stop.id);
  const stopAnswers = stopAnswerEntry?.answers ?? [];
  const allCompletions = stopAnswerEntry?.completions ?? [];
  const stopCompletions = allCompletions.filter(c => c.proof_photo_url);

  return (
    <div className={`admin-stop-row${expanded ? ' open' : ''}`}>
      <button className="admin-stop-header" onClick={() => setExpanded(v => !v)}>
        <span className="admin-stop-year">{stop.year}</span>
        <span className="admin-stop-title">{stop.title}</span>
        {isGoal && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
            background: 'rgba(168,85,247,0.2)', color: '#a855f7',
            border: '1px solid rgba(168,85,247,0.5)', lineHeight: 1.4,
          }}>🏁 Mål</span>
        )}
        <span className="admin-stop-pts">{points}p</span>
        {(allCompletions.length > 0 || stopAnswers.length > 0) && (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {allCompletions.map(c => (
              <span key={`c-${c.team_id}`} style={{
                fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                background: '#10b981', color: '#fff', lineHeight: 1.4,
              }}>
                ✓ {c.team_name}
              </span>
            ))}
            {stopAnswers
              .filter(a => !allCompletions.some(c => c.team_id === a.team))
              .map(a => (
                <span key={`a-${a.team}`} style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                  background: 'rgba(59,130,246,0.2)', color: '#3b82f6',
                  border: '1px solid rgba(59,130,246,0.5)', lineHeight: 1.4,
                }}>
                  ✎ {a.team_name}
                </span>
              ))}
          </span>
        )}
        <span className="admin-stop-chevron">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="admin-stop-body">
          <div style={{ marginBottom: 16 }}>
            <p className="admin-answers-heading" style={{ marginBottom: 8 }}>
              Fullført av {allCompletions.length === 0 && <span className="admin-no-answers">(ingen ennå)</span>}
            </p>
            {allCompletions.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {allCompletions.map(c => (
                  <div key={c.team_id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'rgba(16,185,129,0.12)',
                    border: '1px solid rgba(16,185,129,0.35)',
                    borderRadius: 8,
                    padding: '5px 10px',
                  }}>
                    <span style={{ color: '#10b981', fontSize: 14 }}>✓</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{c.team_name}</span>
                    <span style={{ opacity: 0.55, fontSize: 11 }}>
                      {new Date(c.completed_at).toLocaleString('nb-NO', {
                        day: '2-digit', month: '2-digit',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#f59e0b',
                      marginLeft: 2,
                    }}>
                      {c.points_awarded}p
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="admin-label">Poeng</label>
          <input
            type="number"
            className="admin-input"
            value={points}
            min={0}
            onChange={e => setPoints(e.target.value)}
          />

          <label className="admin-label" style={{ marginTop: 12 }}>Utfordring</label>
          <textarea
            className="admin-input admin-textarea"
            value={challenge}
            onChange={e => setChallenge(e.target.value)}
            rows={4}
          />

          <label className="admin-label" style={{ marginTop: 12 }}>Spørsmål (valgfritt)</label>
          <textarea
            className="admin-input admin-textarea"
            placeholder="Skriv et spørsmål lagene må svare på..."
            value={question}
            onChange={e => setQuestion(e.target.value)}
            rows={2}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
            <div>
              <label className="admin-label">Breddegrad</label>
              <input
                type="number"
                step="any"
                className="admin-input"
                value={lat}
                onChange={e => setLat(e.target.value)}
                placeholder="64.1466"
              />
            </div>
            <div>
              <label className="admin-label">Lengdegrad</label>
              <input
                type="number"
                step="any"
                className="admin-input"
                value={lng}
                onChange={e => setLng(e.target.value)}
                placeholder="-21.9300"
              />
            </div>
          </div>

          {msg && (
            <p className={`admin-save-msg ${msg === 'Lagret!' ? 'success' : ''}`}>{msg}</p>
          )}

          <button
            className="admin-btn"
            onClick={handleSave}
            disabled={saving || !isDirty}
          >
            {saving ? 'Lagrer...' : 'Lagre endringer'}
          </button>

          {/* ── Image upload ── */}
          <div className="admin-image-section">
            <p className="admin-answers-heading">Bilde</p>

            {(imagePreview || resolveImageUrl(currentImageUrl)) && (
              <div className="admin-image-preview-wrap">
                <img
                  src={imagePreview || resolveImageUrl(currentImageUrl)}
                  alt="Forhåndsvisning"
                  className="admin-image-preview"
                />
                {imagePreview && (
                  <span className="admin-image-new-badge">Ny</span>
                )}
              </div>
            )}

            <label className="admin-image-upload-label">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="admin-image-file-input"
                onChange={handleFileChange}
              />
              {currentImageUrl ? 'Bytt bilde' : 'Velg bilde'}
            </label>

            {imageFile && (
              <button
                className="admin-btn"
                onClick={handleUpload}
                disabled={uploading}
                style={{ marginTop: 8 }}
              >
                {uploading ? 'Laster opp...' : 'Last opp bilde'}
              </button>
            )}

            {uploadMsg && (
              <p className={`admin-save-msg ${uploadMsg === 'Bilde lastet opp!' ? 'success' : ''}`}>
                {uploadMsg}
              </p>
            )}
          </div>

          <div className="admin-answers-section">
            <p className="admin-answers-heading">
              Svar fra lagene {stopAnswers.length === 0 && <span className="admin-no-answers">(ingen ennå)</span>}
            </p>
            {stopAnswers.map(answer => (
              <div key={answer.id} className="admin-answer-row">
                <span className="admin-answer-team">{answer.team_name}</span>
                <span className="admin-answer-text">{answer.answer_text}</span>
              </div>
            ))}
          </div>

          {stopCompletions.length > 0 && (
            <div className="admin-answers-section" style={{ marginTop: 12 }}>
              <p className="admin-answers-heading">Bevisbilder fra lagene</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                {stopCompletions.map(c => (
                  <a
                    key={c.team_id}
                    href={resolveImageUrl(c.proof_photo_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', textAlign: 'center' }}
                  >
                    <img
                      src={resolveImageUrl(c.proof_photo_url)}
                      alt={`Bevis fra ${c.team_name}`}
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: 'cover',
                        borderRadius: 8,
                        display: 'block',
                        border: '2px solid rgba(255,255,255,0.1)',
                      }}
                    />
                    <span className="admin-answer-team" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                      {c.team_name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <button
            className={`admin-btn${isGoal ? '-secondary' : ''}`}
            onClick={() => onSetGoal(isGoal ? null : stop.id)}
            disabled={goalSaving}
            style={{ marginTop: 8, background: isGoal ? 'rgba(168,85,247,0.15)' : undefined, borderColor: isGoal ? '#a855f7' : undefined, color: isGoal ? '#a855f7' : undefined }}
          >
            {isGoal ? '🏁 Fjern som mål' : '🏁 Sett som mål'}
          </button>

          <button
            className="admin-delete-btn"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Sletter...' : '🗑 Slett stasjon'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── New stop form ────────────────────────────────────────────────────────────

const EMPTY_STOP = {
  title: '', year: '', latitude: '', longitude: '',
  description: '', points: '10', order: '0',
  challenge_text: '', question: '',
};

function NewStopForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_STOP);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      const payload = {
        ...form,
        year: parseInt(form.year, 10),
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        points: parseInt(form.points, 10),
        order: parseInt(form.order, 10),
      };
      const res = await stopsApi.create(payload, ADMIN_PASSWORD);
      setMsg('Stasjon opprettet!');
      setForm(EMPTY_STOP);
      onCreated(res.data);
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        const first = Object.entries(detail)[0];
        setMsg(first ? `${first[0]}: ${first[1]}` : 'Noe gikk galt.');
      } else {
        setMsg('Noe gikk galt.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-new-stop">
      <button
        className={`admin-new-stop-toggle${open ? ' open' : ''}`}
        onClick={() => { setOpen(v => !v); setMsg(''); }}
      >
        <span>+ Ny stasjon</span>
        <span className="admin-stop-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <form className="admin-new-stop-form" onSubmit={handleSubmit}>
          <div className="admin-new-stop-grid">
            <div className="admin-field">
              <label className="admin-label">Tittel *</label>
              <input className="admin-input" value={form.title} onChange={set('title')} required />
            </div>
            <div className="admin-field">
              <label className="admin-label">År *</label>
              <input className="admin-input" type="number" value={form.year} onChange={set('year')} required />
            </div>
            <div className="admin-field">
              <label className="admin-label">Breddegrad *</label>
              <input className="admin-input" type="number" step="any" value={form.latitude} onChange={set('latitude')} placeholder="64.1466" required />
            </div>
            <div className="admin-field">
              <label className="admin-label">Lengdegrad *</label>
              <input className="admin-input" type="number" step="any" value={form.longitude} onChange={set('longitude')} placeholder="-21.9300" required />
            </div>
            <div className="admin-field">
              <label className="admin-label">Poeng</label>
              <input className="admin-input" type="number" value={form.points} onChange={set('points')} min={0} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Rekkefølge</label>
              <input className="admin-input" type="number" value={form.order} onChange={set('order')} />
            </div>
          </div>

          <label className="admin-label" style={{ marginTop: 12 }}>Beskrivelse</label>
          <textarea className="admin-input admin-textarea" rows={2} value={form.description} onChange={set('description')} />

          <label className="admin-label" style={{ marginTop: 12 }}>Utfordring</label>
          <textarea className="admin-input admin-textarea" rows={3} value={form.challenge_text} onChange={set('challenge_text')} />

          <label className="admin-label" style={{ marginTop: 12 }}>Spørsmål (valgfritt)</label>
          <textarea className="admin-input admin-textarea" rows={2} value={form.question} onChange={set('question')} />

          {msg && (
            <p className={`admin-save-msg ${msg === 'Stasjon opprettet!' ? 'success' : ''}`}>{msg}</p>
          )}
          <button type="submit" className="admin-btn" disabled={saving}>
            {saving ? 'Oppretter...' : 'Opprett stasjon'}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Generic challenge row ────────────────────────────────────────────────────

function GenericChallengeRow({ challenge, answersData, onDeleted }) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(challenge.title || '');
  const [description, setDescription] = useState(challenge.description || '');
  const [points, setPoints] = useState(String(challenge.points));
  const [challengeText, setChallengeText] = useState(challenge.challenge_text || '');
  const [isActive, setIsActive] = useState(challenge.is_active);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [currentImageUrl, setCurrentImageUrl] = useState(challenge.image_url || '');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  const isDirty =
    title !== (challenge.title || '') ||
    description !== (challenge.description || '') ||
    points !== String(challenge.points) ||
    challengeText !== (challenge.challenge_text || '') ||
    isActive !== challenge.is_active;

  const answers = answersData?.answers ?? [];
  const completions = answersData?.completions ?? challenge.completed_by_teams?.map(id => ({ team_id: id })) ?? [];
  const completionsWithPhoto = (answersData?.completions ?? []).filter(c => c.proof_photo_url);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await challengesApi.update(challenge.id, {
        title, description,
        points: parseInt(points, 10),
        challenge_text: challengeText,
        is_active: isActive,
      }, ADMIN_PASSWORD);
      setMsg('Lagret!');
    } catch (err) {
      setMsg(err.response?.data?.error || 'Noe gikk galt.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setUploadMsg('');
  };

  const handleUpload = async () => {
    if (!imageFile) return;
    setUploading(true);
    setUploadMsg('');
    try {
      const res = await challengesApi.uploadImage(challenge.id, imageFile, ADMIN_PASSWORD);
      setCurrentImageUrl(res.data.image_url || '');
      setImageFile(null);
      setImagePreview(null);
      setUploadMsg('Bilde lastet opp!');
    } catch (err) {
      setUploadMsg(err.response?.data?.error || 'Opplasting feilet.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Slett "${challenge.title}"? Dette kan ikke angres.`)) return;
    setDeleting(true);
    try {
      await challengesApi.delete(challenge.id, ADMIN_PASSWORD);
      onDeleted(challenge.id);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Sletting feilet.');
      setDeleting(false);
    }
  };

  return (
    <div className={`admin-stop-row${expanded ? ' open' : ''}`}>
      <button className="admin-stop-header" onClick={() => setExpanded(v => !v)}>
        <span className="admin-stop-title">{challenge.title}</span>
        <span className="admin-stop-pts">{challenge.points}p</span>
        {!isActive && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
            background: 'rgba(239,68,68,0.2)', color: '#ef4444',
            border: '1px solid rgba(239,68,68,0.5)', lineHeight: 1.4,
          }}>Inaktiv</span>
        )}
        {((answersData?.completions ?? []).length > 0 || answers.length > 0) && (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {(answersData?.completions ?? []).map(c => (
              <span key={`c-${c.team_id}`} style={{
                fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                background: '#10b981', color: '#fff', lineHeight: 1.4,
              }}>
                ✓ {c.team_name}
              </span>
            ))}
            {answers
              .filter(a => !(answersData?.completions ?? []).some(c => c.team_id === a.team))
              .map(a => (
                <span key={`a-${a.team}`} style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                  background: 'rgba(59,130,246,0.2)', color: '#3b82f6',
                  border: '1px solid rgba(59,130,246,0.5)', lineHeight: 1.4,
                }}>
                  ✎ {a.team_name}
                </span>
              ))}
          </span>
        )}
        <span className="admin-stop-chevron">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="admin-stop-body">
          <div style={{ marginBottom: 16 }}>
            <p className="admin-answers-heading" style={{ marginBottom: 8 }}>
              Fullført av {(answersData?.completions ?? []).length === 0 && <span className="admin-no-answers">(ingen ennå)</span>}
            </p>
            {(answersData?.completions ?? []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(answersData?.completions ?? []).map(c => (
                  <div key={c.team_id} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.35)',
                    borderRadius: 8, padding: '5px 10px',
                  }}>
                    <span style={{ color: '#10b981', fontSize: 14 }}>✓</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{c.team_name}</span>
                    <span style={{ opacity: 0.55, fontSize: 11 }}>
                      {new Date(c.completed_at).toLocaleString('nb-NO', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', marginLeft: 2 }}>
                      {c.points_awarded}p
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="admin-label">Tittel</label>
          <input className="admin-input" value={title} onChange={e => setTitle(e.target.value)} />

          <label className="admin-label" style={{ marginTop: 12 }}>Beskrivelse</label>
          <textarea className="admin-input admin-textarea" value={description} onChange={e => setDescription(e.target.value)} rows={2} />

          <label className="admin-label" style={{ marginTop: 12 }}>Poeng</label>
          <input type="number" className="admin-input" value={points} min={0} onChange={e => setPoints(e.target.value)} />

          <label className="admin-label" style={{ marginTop: 12 }}>Utfordring</label>
          <textarea className="admin-input admin-textarea" value={challengeText} onChange={e => setChallengeText(e.target.value)} rows={3} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <label className="admin-label" style={{ margin: 0 }}>Aktiv</label>
            <button
              className={`admin-toggle-btn${isActive ? ' on' : ''}`}
              onClick={() => setIsActive(v => !v)}
              style={{ minWidth: 48 }}
            >
              {isActive ? 'Ja' : 'Nei'}
            </button>
          </div>

          {msg && (
            <p className={`admin-save-msg ${msg === 'Lagret!' ? 'success' : ''}`}>{msg}</p>
          )}
          <button className="admin-btn" onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? 'Lagrer...' : 'Lagre endringer'}
          </button>

          <div className="admin-image-section">
            <p className="admin-answers-heading">Bilde</p>
            {(imagePreview || resolveImageUrl(currentImageUrl)) && (
              <div className="admin-image-preview-wrap">
                <img
                  src={imagePreview || resolveImageUrl(currentImageUrl)}
                  alt="Forhåndsvisning"
                  className="admin-image-preview"
                />
                {imagePreview && <span className="admin-image-new-badge">Ny</span>}
              </div>
            )}
            <label className="admin-image-upload-label">
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="admin-image-file-input" onChange={handleFileChange} />
              {currentImageUrl ? 'Bytt bilde' : 'Velg bilde'}
            </label>
            {imageFile && (
              <button className="admin-btn" onClick={handleUpload} disabled={uploading} style={{ marginTop: 8 }}>
                {uploading ? 'Laster opp...' : 'Last opp bilde'}
              </button>
            )}
            {uploadMsg && (
              <p className={`admin-save-msg ${uploadMsg === 'Bilde lastet opp!' ? 'success' : ''}`}>{uploadMsg}</p>
            )}
          </div>

          <div className="admin-answers-section">
            <p className="admin-answers-heading">
              Svar fra lagene {answers.length === 0 && <span className="admin-no-answers">(ingen ennå)</span>}
            </p>
            {answers.map(answer => (
              <div key={answer.id} className="admin-answer-row">
                <span className="admin-answer-team">{answer.team_name}</span>
                <span className="admin-answer-text">{answer.answer_text}</span>
              </div>
            ))}
          </div>

          {completionsWithPhoto.length > 0 && (
            <div className="admin-answers-section" style={{ marginTop: 12 }}>
              <p className="admin-answers-heading">Bevisbilder fra lagene</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                {completionsWithPhoto.map(c => (
                  <a
                    key={c.team_id}
                    href={resolveImageUrl(c.proof_photo_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', textAlign: 'center' }}
                  >
                    <img
                      src={resolveImageUrl(c.proof_photo_url)}
                      alt={`Bevis fra ${c.team_name}`}
                      style={{
                        width: 120, height: 120, objectFit: 'cover', borderRadius: 8,
                        display: 'block', border: '2px solid rgba(255,255,255,0.1)',
                      }}
                    />
                    <span className="admin-answer-team" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                      {c.team_name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <button className="admin-delete-btn" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Sletter...' : '🗑 Slett utfordring'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── New challenge form ──────────────────────────────────────────────────────

const EMPTY_CHALLENGE = {
  title: '', description: '', points: '5', challenge_text: '',
};

function NewChallengeForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CHALLENGE);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      const payload = {
        ...form,
        points: parseInt(form.points, 10),
      };
      const res = await challengesApi.create(payload, ADMIN_PASSWORD);
      setMsg('Utfordring opprettet!');
      setForm(EMPTY_CHALLENGE);
      onCreated(res.data);
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        const first = Object.entries(detail)[0];
        setMsg(first ? `${first[0]}: ${first[1]}` : 'Noe gikk galt.');
      } else {
        setMsg('Noe gikk galt.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-new-stop">
      <button
        className={`admin-new-stop-toggle${open ? ' open' : ''}`}
        onClick={() => { setOpen(v => !v); setMsg(''); }}
      >
        <span>+ Ny utfordring</span>
        <span className="admin-stop-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <form className="admin-new-stop-form" onSubmit={handleSubmit}>
          <div className="admin-new-stop-grid">
            <div className="admin-field">
              <label className="admin-label">Tittel *</label>
              <input className="admin-input" value={form.title} onChange={set('title')} required />
            </div>
            <div className="admin-field">
              <label className="admin-label">Poeng</label>
              <input className="admin-input" type="number" value={form.points} onChange={set('points')} min={0} />
            </div>
          </div>

          <label className="admin-label" style={{ marginTop: 12 }}>Beskrivelse</label>
          <textarea className="admin-input admin-textarea" rows={2} value={form.description} onChange={set('description')} />

          <label className="admin-label" style={{ marginTop: 12 }}>Utfordring</label>
          <textarea className="admin-input admin-textarea" rows={3} value={form.challenge_text} onChange={set('challenge_text')} />

          {msg && (
            <p className={`admin-save-msg ${msg === 'Utfordring opprettet!' ? 'success' : ''}`}>{msg}</p>
          )}
          <button type="submit" className="admin-btn" disabled={saving}>
            {saving ? 'Oppretter...' : 'Opprett utfordring'}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Main admin page ──────────────────────────────────────────────────────────

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');

  const [currentEndTime, setCurrentEndTime] = useState(null);
  const [dateInput, setDateInput] = useState('');
  const [timeInput, setTimeInput] = useState('');
  const [timeSaving, setTimeSaving] = useState(false);
  const [timeMsg, setTimeMsg] = useState('');

  const [radiusInput, setRadiusInput] = useState('70');
  const [radiusSaving, setRadiusSaving] = useState(false);
  const [radiusMsg, setRadiusMsg] = useState('');

  const [hidePoints, setHidePoints] = useState(false);
  const [hidePointsSaving, setHidePointsSaving] = useState(false);

  const [hideScoreboard, setHideScoreboard] = useState(false);
  const [hideScoreboardSaving, setHideScoreboardSaving] = useState(false);

  const [goalStopId, setGoalStopId] = useState(null);
  const [goalSaving, setGoalSaving] = useState(false);

  const [stops, setStops] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [stopsLoading, setStopsLoading] = useState(true);

  const [genericChallenges, setGenericChallenges] = useState([]);
  const [genericAnswers, setGenericAnswers] = useState([]);
  const [genericLoading, setGenericLoading] = useState(true);

  const [teams, setTeams] = useState([]);
  const [awards, setAwards] = useState([]);
  const [awardTeam, setAwardTeam] = useState('');
  const [awardPoints, setAwardPoints] = useState('');
  const [awardReason, setAwardReason] = useState('');
  const [awardSaving, setAwardSaving] = useState(false);
  const [awardMsg, setAwardMsg] = useState('');

  const handleLogin = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setAuthed(true);
  };

  const loadData = useCallback(() => {
    settingsApi.get().then(res => {
      if (res.data.end_time) {
        setCurrentEndTime(res.data.end_time);
        const d = new Date(res.data.end_time);
        setDateInput(d.toLocaleDateString('sv-SE'));
        setTimeInput(d.toTimeString().slice(0, 5));
      }
      if (res.data.stop_radius != null) {
        setRadiusInput(String(res.data.stop_radius));
      }
      if (res.data.hide_points != null) {
        setHidePoints(res.data.hide_points);
      }
      if (res.data.hide_scoreboard != null) {
        setHideScoreboard(res.data.hide_scoreboard);
      }
      setGoalStopId(res.data.goal_stop_id ?? null);
    }).catch(() => {});

    Promise.all([
      stopsApi.adminList(ADMIN_PASSWORD),
      stopsApi.adminAnswers(ADMIN_PASSWORD),
    ]).then(([stopsRes, answersRes]) => {
      const sorted = [...stopsRes.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setStops(sorted);
      setAnswers(answersRes.data);
    }).catch(() => {}).finally(() => setStopsLoading(false));

    Promise.all([
      challengesApi.adminList(ADMIN_PASSWORD),
      challengesApi.adminAnswers(ADMIN_PASSWORD),
    ]).then(([listRes, answersRes]) => {
      setGenericChallenges(listRes.data);
      setGenericAnswers(answersRes.data);
    }).catch(() => {}).finally(() => setGenericLoading(false));

    teamsApi.getAll().then(res => setTeams(res.data)).catch(() => {});
    manualPointsApi.getAll(ADMIN_PASSWORD).then(res => setAwards(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadData();
  }, [authed, loadData]);

  const handleSaveTime = async (e) => {
    e.preventDefault();
    if (!dateInput || !timeInput) { setTimeMsg('Fyll inn dato og tid.'); return; }
    setTimeSaving(true);
    setTimeMsg('');
    try {
      const res = await settingsApi.setEndTime(`${dateInput}T${timeInput}:00`, ADMIN_PASSWORD);
      setCurrentEndTime(res.data.end_time);
      setTimeMsg('Lagret!');
    } catch (err) {
      setTimeMsg(err.response?.data?.error || 'Noe gikk galt.');
    } finally {
      setTimeSaving(false);
    }
  };

  const handleClearTime = async () => {
    setTimeSaving(true);
    setTimeMsg('');
    try {
      await settingsApi.setEndTime(null, ADMIN_PASSWORD);
      setCurrentEndTime(null);
      setDateInput('');
      setTimeInput('');
      setTimeMsg('Nedtelling fjernet.');
    } catch {
      setTimeMsg('Noe gikk galt.');
    } finally {
      setTimeSaving(false);
    }
  };

  const handleAward = async (e) => {
    e.preventDefault();
    const pts = parseInt(awardPoints, 10);
    if (!awardTeam) { setAwardMsg('Velg et lag.'); return; }
    if (isNaN(pts) || pts === 0) { setAwardMsg('Oppgi et antall poeng (ikke 0).'); return; }
    setAwardSaving(true);
    setAwardMsg('');
    try {
      const res = await manualPointsApi.award(parseInt(awardTeam, 10), pts, awardReason, ADMIN_PASSWORD);
      setAwards(prev => [res.data, ...prev]);
      setAwardPoints('');
      setAwardReason('');
      setAwardMsg('Tildelt!');
    } catch (err) {
      setAwardMsg(err.response?.data?.error || 'Noe gikk galt.');
    } finally {
      setAwardSaving(false);
    }
  };

  const handleRemoveAward = async (id) => {
    if (!window.confirm('Fjerne denne tildelingen?')) return;
    try {
      await manualPointsApi.remove(id, ADMIN_PASSWORD);
      setAwards(prev => prev.filter(a => a.id !== id));
    } catch {
      // silently ignore
    }
  };

  const handleSetGoalStop = async (stopId) => {
    setGoalSaving(true);
    try {
      const res = await settingsApi.setGoalStop(stopId, ADMIN_PASSWORD);
      setGoalStopId(res.data.goal_stop_id ?? null);
    } catch {
      // silently ignore
    } finally {
      setGoalSaving(false);
    }
  };

  const handleToggleHidePoints = async (newValue) => {
    setHidePointsSaving(true);
    try {
      const res = await settingsApi.setHidePoints(newValue, ADMIN_PASSWORD);
      setHidePoints(res.data.hide_points);
    } catch {
      setHidePoints(!newValue);
    } finally {
      setHidePointsSaving(false);
    }
  };

  const handleToggleHideScoreboard = async (newValue) => {
    setHideScoreboardSaving(true);
    try {
      const res = await settingsApi.setHideScoreboard(newValue, ADMIN_PASSWORD);
      setHideScoreboard(res.data.hide_scoreboard);
    } catch {
      setHideScoreboard(!newValue);
    } finally {
      setHideScoreboardSaving(false);
    }
  };

  const handleSaveRadius = async (e) => {
    e.preventDefault();
    const val = parseInt(radiusInput, 10);
    if (isNaN(val) || val < 10) { setRadiusMsg('Radius må være minst 10 meter.'); return; }
    setRadiusSaving(true);
    setRadiusMsg('');
    try {
      const res = await settingsApi.setStopRadius(val, ADMIN_PASSWORD);
      setRadiusInput(String(res.data.stop_radius));
      setRadiusMsg('Lagret!');
    } catch (err) {
      setRadiusMsg(err.response?.data?.error || 'Noe gikk galt.');
    } finally {
      setRadiusSaving(false);
    }
  };

  const [resetMsg, setResetMsg] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleResetScores = async () => {
    if (!window.confirm('Er du sikker? Dette sletter ALL progresjon for alle lag og kan ikke angres.')) return;
    setResetting(true);
    setResetMsg('');
    try {
      await adminApi.resetScores(ADMIN_PASSWORD);
      localStorage.removeItem('animated_stops');
      setAwards([]);
      setAnswers([]);
      setGenericAnswers([]);
      setResetMsg('Alle resultater er nullstilt!');
      loadData();
    } catch (err) {
      setResetMsg(err.response?.data?.error || 'Noe gikk galt.');
    } finally {
      setResetting(false);
    }
  };

  if (!authed) return <LoginCard onLogin={handleLogin} />;

  return (
    <div className="page admin-page admin-page-full">
      <div className="admin-scroll">

        {/* ── Countdown ── */}
        <div className="admin-card">
          <h2 className="admin-section-title">Nedtelling</h2>

          {currentEndTime && (
            <div className="admin-current">
              <span className="admin-label">Nåværende sluttid</span>
              <span className="admin-value">
                {new Date(currentEndTime).toLocaleString('nb-NO', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </div>
          )}

          <form onSubmit={handleSaveTime} className="admin-form">
            <label className="admin-label">Dato</label>
            <input
              type="date"
              className="admin-input"
              value={dateInput}
              onChange={e => setDateInput(e.target.value)}
            />
            <label className="admin-label" style={{ marginTop: 12 }}>Klokkeslett</label>
            <input
              type="time"
              className="admin-input"
              value={timeInput}
              onChange={e => setTimeInput(e.target.value)}
            />
            {timeMsg && (
              <p className={`admin-save-msg ${timeMsg === 'Lagret!' ? 'success' : ''}`}>{timeMsg}</p>
            )}
            <button type="submit" className="admin-btn" disabled={timeSaving}>
              {timeSaving ? 'Lagrer...' : 'Sett nedtelling'}
            </button>
          </form>

          {currentEndTime && (
            <button className="admin-btn-secondary" onClick={handleClearTime} disabled={timeSaving}>
              Fjern nedtelling
            </button>
          )}
        </div>

        {/* ── Visibility ── */}
        <div className="admin-card" style={{ marginTop: 16 }}>
          <h2 className="admin-section-title">Synlighet</h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <p className="admin-label" style={{ marginBottom: 2 }}>Skjul poengverdier</p>
              <p className="admin-subtitle" style={{ fontSize: 12 }}>
                Lagene ser ikke hvor mye hver utfordring er verdt, og poengtavlen viser ikke totalpoeng.
              </p>
            </div>
            <button
              className={`admin-toggle-btn${hidePoints ? ' on' : ''}`}
              onClick={() => !hidePointsSaving && handleToggleHidePoints(!hidePoints)}
              disabled={hidePointsSaving}
              aria-pressed={hidePoints}
            >
              {hidePoints ? 'På' : 'Av'}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginTop: 16 }}>
            <div>
              <p className="admin-label" style={{ marginBottom: 2 }}>Skjul andre lags poeng</p>
              <p className="admin-subtitle" style={{ fontSize: 12 }}>
                Lagene ser kun sin egen plassering og poeng på poengtavlen, ikke de andre lagene.
              </p>
            </div>
            <button
              className={`admin-toggle-btn${hideScoreboard ? ' on' : ''}`}
              onClick={() => !hideScoreboardSaving && handleToggleHideScoreboard(!hideScoreboard)}
              disabled={hideScoreboardSaving}
              aria-pressed={hideScoreboard}
            >
              {hideScoreboard ? 'På' : 'Av'}
            </button>
          </div>
        </div>

        {/* ── Proximity radius ── */}
        <div className="admin-card" style={{ marginTop: 16 }}>
          <h2 className="admin-section-title">Nærhetssirkel</h2>
          <p className="admin-subtitle" style={{ marginBottom: 12 }}>
            Radius på den gule sirkelen på kartet. Lagene kan åpne utfordringen når de er innenfor radius + 10m.
          </p>
          <form onSubmit={handleSaveRadius} className="admin-form">
            <label className="admin-label">Radius (meter)</label>
            <input
              type="number"
              className="admin-input"
              value={radiusInput}
              min={10}
              onChange={e => setRadiusInput(e.target.value)}
            />
            <p className="admin-subtitle" style={{ fontSize: 12, marginTop: 4 }}>
              Utfordring låses opp innen {(parseInt(radiusInput, 10) || 70) + 10}m
            </p>
            {radiusMsg && (
              <p className={`admin-save-msg ${radiusMsg === 'Lagret!' ? 'success' : ''}`}>{radiusMsg}</p>
            )}
            <button type="submit" className="admin-btn" disabled={radiusSaving}>
              {radiusSaving ? 'Lagrer...' : 'Sett radius'}
            </button>
          </form>
        </div>

        {/* ── Manual points ── */}
        <div className="admin-card" style={{ marginTop: 16 }}>
          <h2 className="admin-section-title">Manuelle poeng</h2>
          <form onSubmit={handleAward} className="admin-form">
            <label className="admin-label">Lag</label>
            <select
              className="admin-input"
              value={awardTeam}
              onChange={e => setAwardTeam(e.target.value)}
            >
              <option value="">— Velg lag —</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            <label className="admin-label" style={{ marginTop: 12 }}>Poeng (negativt for trekk)</label>
            <input
              type="number"
              className="admin-input"
              placeholder="f.eks. 50 eller -20"
              value={awardPoints}
              onChange={e => setAwardPoints(e.target.value)}
            />

            <label className="admin-label" style={{ marginTop: 12 }}>Grunn (valgfritt)</label>
            <input
              type="text"
              className="admin-input"
              placeholder="f.eks. Bonuspoeng for kreativitet"
              value={awardReason}
              onChange={e => setAwardReason(e.target.value)}
            />

            {awardMsg && (
              <p className={`admin-save-msg ${awardMsg === 'Tildelt!' ? 'success' : ''}`}>{awardMsg}</p>
            )}
            <button type="submit" className="admin-btn" disabled={awardSaving}>
              {awardSaving ? 'Tildeler...' : 'Gi poeng'}
            </button>
          </form>

          {awards.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p className="admin-answers-heading">Tidligere tildelinger</p>
              {awards.map(a => (
                <div key={a.id} className="admin-answer-row" style={{ alignItems: 'center' }}>
                  <span className="admin-answer-team">{a.team_name}</span>
                  <span className="admin-answer-text" style={{ color: a.points >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                    {a.points >= 0 ? '+' : ''}{a.points}p
                  </span>
                  {a.reason && <span className="admin-answer-text">{a.reason}</span>}
                  <button
                    onClick={() => handleRemoveAward(a.id)}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16 }}
                    title="Fjern tildeling"
                  >✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Stops ── */}
        <div className="admin-card" style={{ marginTop: 16 }}>
          <h2 className="admin-section-title">Stasjoner</h2>

          <NewStopForm
            onCreated={(newStop) => {
              setStops(prev => [...prev, newStop].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
            }}
          />

          {stopsLoading ? (
            <div className="admin-loading" style={{ marginTop: 16 }}>Laster stasjoner...</div>
          ) : stops.length === 0 ? (
            <p className="admin-subtitle" style={{ marginTop: 12 }}>Ingen stasjoner ennå.</p>
          ) : (
            <div className="admin-stops-list" style={{ marginTop: 16 }}>
              {stops.map(stop => (
                <StopRow
                  key={stop.id}
                  stop={stop}
                  answers={answers}
                  onDeleted={(id) => setStops(prev => prev.filter(s => s.id !== id))}
                  isGoal={stop.id === goalStopId}
                  onSetGoal={handleSetGoalStop}
                  goalSaving={goalSaving}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Generic challenges ── */}
        <div className="admin-card" style={{ marginTop: 16 }}>
          <h2 className="admin-section-title">Utfordringer</h2>

          <NewChallengeForm
            onCreated={(newCh) => setGenericChallenges(prev => [...prev, newCh])}
          />

          {genericLoading ? (
            <div className="admin-loading" style={{ marginTop: 16 }}>Laster utfordringer...</div>
          ) : genericChallenges.length === 0 ? (
            <p className="admin-subtitle" style={{ marginTop: 12 }}>Ingen utfordringer ennå.</p>
          ) : (
            <div className="admin-stops-list" style={{ marginTop: 16 }}>
              {genericChallenges.map(ch => (
                <GenericChallengeRow
                  key={ch.id}
                  challenge={ch}
                  answersData={genericAnswers.find(a => a.challenge_id === ch.id)}
                  onDeleted={(id) => setGenericChallenges(prev => prev.filter(c => c.id !== id))}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Danger zone ── */}
        <div className="admin-card" style={{ marginTop: 16, borderColor: 'rgba(239,68,68,0.4)' }}>
          <h2 className="admin-section-title" style={{ color: '#ef4444' }}>Faresone</h2>
          <p className="admin-subtitle" style={{ marginBottom: 12 }}>
            Nullstiller all progresjon: fullførte stasjoner, svar, bevisbilder og manuelle poeng. Kan ikke angres.
          </p>
          {resetMsg && (
            <p className={`admin-save-msg ${resetMsg === 'Alle resultater er nullstilt!' ? 'success' : ''}`} style={{ marginBottom: 8 }}>
              {resetMsg}
            </p>
          )}
          <button
            className="admin-delete-btn"
            onClick={handleResetScores}
            disabled={resetting}
          >
            {resetting ? 'Nullstiller...' : '⚠ Nullstill alle resultater'}
          </button>
        </div>

      </div>
    </div>
  );
}
